// Team & Rollen › „Support durch den Betreiber“ (h-ui).
//
// Die Radioleitung erlaubt dem Betreiber für 24 Stunden oder 7 Tage, sich
// zum Helfen in ihr Radio zu schalten – und sieht jeden Zugriff im Protokoll.
// Ohne Freigabe geht nur ein Notfall-Zugriff, über den sie sofort Bescheid bekommt.

import { useEffect, useState } from "react";
import { tarifApi } from "../api";
import type { SupportFreigabe, SupportZugriff } from "../types";
import { errorText } from "./streaming-ui";
import { Badge, Button, Card } from "./ui";
import { Sym } from "./social-ui";
import { datumText } from "./hosting-ui";

export function SupportFreigabeKarte(): JSX.Element {
  const [freigabe, setFreigabe] = useState<SupportFreigabe | null>(null);
  const [zugriffe, setZugriffe] = useState<SupportZugriff[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = (): void => {
    tarifApi.freigabe().then(setFreigabe).catch((err) => setFehler(errorText(err)));
    tarifApi.zugriffe().then(setZugriffe).catch(() => setZugriffe([]));
  };
  useEffect(laden, []);

  const setze = async (stunden: number | null): Promise<void> => {
    setLaeuft(true);
    setFehler(null);
    try {
      const bis = stunden == null ? null : new Date(Date.now() + stunden * 3600_000).toISOString();
      const r = await tarifApi.freigabeSetzen(bis);
      setFreigabe(r && typeof r === "object" && "bis" in r ? r : { bis });
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setLaeuft(false);
    }
  };

  const aktiv = freigabe?.bis ? Date.parse(freigabe.bis) > Date.now() : false;

  return (
    <Card
      title={<span className="inline-flex items-center gap-2"><Sym name="shield" className="h-4 w-4 text-muted" />Support durch den Betreiber</span>}
      description="Erlaubt dem Betreiber, sich zum Helfen in euer Radio zu schalten – für höchstens eine Stunde am Stück. Jeder Zugriff steht unten im Protokoll."
      actions={aktiv ? <Badge tone="success" dot>freigegeben</Badge> : <Badge>nicht freigegeben</Badge>}
      className="mt-5"
    >
      {fehler && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{fehler}</p>}
      <p className="text-sm text-secondary">
        {freigabe === null
          ? "Wird geladen …"
          : aktiv
            ? <>Freigegeben bis <b className="text-primary">{datumText(freigabe.bis, true)}</b>{freigabe.erteilt_von?.name ? ` (erteilt von ${freigabe.erteilt_von.name})` : ""}.</>
            : "Zurzeit nicht freigegeben. Ohne Freigabe kann der Betreiber nur im Notfall zugreifen – dann bekommt ihr sofort eine Nachricht."}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant={aktiv ? "secondary" : "primary"} disabled={laeuft} onClick={() => void setze(24)}>24 Stunden freigeben</Button>
        <Button disabled={laeuft} onClick={() => void setze(24 * 7)}>7 Tage freigeben</Button>
        {aktiv && <Button variant="danger" disabled={laeuft} onClick={() => void setze(null)}>Widerrufen</Button>}
      </div>

      <div className="section-label mb-2 mt-6">Protokoll der Zugriffe</div>
      {zugriffe === null && <p className="text-sm text-muted">Wird geladen …</p>}
      {zugriffe?.length === 0 && <p className="text-sm text-muted">Bisher hat sich der Betreiber nicht in euer Radio geschaltet.</p>}
      {zugriffe && zugriffe.length > 0 && (
        <ZugriffsListe
          zugriffe={zugriffe}
          onBeenden={(id) => {
            tarifApi.zugriffBeenden(id).then(laden).catch((err) => setFehler(errorText(err)));
          }}
        />
      )}
    </Card>
  );
}

/** Liste der Support-Zugriffe – hier für die Radioleitung, am Radio für den Betreiber. */
export function ZugriffsListe({ zugriffe, onBeenden }: { zugriffe: SupportZugriff[]; onBeenden?: (id: string) => void }): JSX.Element {
  return (
    <ul className="divide-y divide-default rounded-lg border border-default text-sm">
      {zugriffe.map((z) => {
        const laeuftNoch = z.laeuft ?? (!z.beendet_at && Date.parse(z.ende) > Date.now());
        return (
          <li key={z.id} className="px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="tabular-nums text-primary">{datumText(z.beginn, true)}</span>
              <Badge tone={z.art === "notfall" ? "warning" : "info"}>{z.art === "notfall" ? "Notfall" : "mit Freigabe"}</Badge>
              {laeuftNoch ? <Badge tone="success" dot>läuft gerade</Badge> : <span className="text-xs text-muted">bis {datumText(z.beendet_at ?? z.ende, true)}</span>}
              {z.betreiber?.name && <span className="text-xs text-muted">· {z.betreiber.name}</span>}
              {laeuftNoch && onBeenden && (
                <button type="button" onClick={() => onBeenden(z.id)} className="btn btn-danger btn-sm ml-auto">Jetzt beenden</button>
              )}
            </div>
            <p className="mt-1 break-words text-secondary">{z.grund}</p>
            {z.aktionen && z.aktionen.length > 0 && (
              <details className="mt-1.5 text-xs">
                <summary className="cursor-pointer text-muted hover:text-primary">{z.aktionen.length} {z.aktionen.length === 1 ? "Änderung" : "Änderungen"} ansehen</summary>
                <ul className="mt-1.5 space-y-0.5 font-mono text-[0.72rem] text-secondary">
                  {z.aktionen.map((a, i) => (
                    <li key={i} className="break-all">
                      <span className="text-muted">{new Date(a.zeit).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span> {a.was}
                      {a.status != null && a.status >= 400 && <span className="text-red-600 dark:text-red-400"> ({a.status})</span>}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </li>
        );
      })}
    </ul>
  );
}
